import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@mindstonehq/ui";
import React, { useState } from "react";
import { Form } from "react-router";
import { useMutation } from "@apollo/client/react";
import { FlagIcon } from "@heroicons/react/20/solid";
import metrics from "~/utils/metrics";
import { LoadingIcon } from "~/components/icons";
import { REPORT_RESOURCE_MUTATION } from "../annotate-mutations";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

const options: Array<{ value: string; label: MessageDescriptor }> = [
  {
    value: "scraping",
    label: msg`Resource did not load correctly/completely`,
  },
  {
    value: "copyright",
    label: msg`Resource may be in breach of copyright`,
  },
  {
    value: "factualerrors",
    label: msg`Resource contains factual errors`,
  },
  {
    value: "other",
    label: msg`Other`,
  },
];
export function DialogReportArticle(
  props: React.PropsWithChildren<{
    articleID: string;
    onOpenChange: (open: Boolean) => any;
  }>,
) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [reportResource, { loading, error }] = useMutation(
    REPORT_RESOURCE_MUTATION,
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        props.onOpenChange(o);
      }}
    >
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center flex-row space-x-2">
            <FlagIcon className="h-6 w-6" aria-hidden="true" />
            <span><Trans>Report this resource</Trans></span>
          </DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            metrics.track("article_report", {
              view: "annotate_action",
              article_id: props.articleID,
            });
            return reportResource({
              variables: {
                id: props.articleID,
                details: (formData.get("comment") as string) || "",
                type: (formData.get("reason") as string) || "",
              },
            })
              .then(() => {
                setOpen(false);
                props.onOpenChange(false);
              })
              .catch(() => {
                toast.error(
                  t(msg`We couldn't report this resource. Please try again later.`),
                );
                throw error;
              });
          }}
        >
          <div className={"my-4 flex flex-col space-y-4 w-full"}>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="reason"><Trans>What's the reason?</Trans></Label>
              <Select name="reason" required={true}>
                <SelectTrigger>
                  <SelectValue placeholder={t(msg`Reason`)} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((x) => {
                    return (
                      <SelectItem key={x.value} value={x.value}>
                        {t(x.label)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="comment">
                <Trans>Any details that you think could help us?</Trans>
              </Label>
              <Textarea
                id="comment"
                placeholder={t(msg`Any details which might help us fix it`)}
                name="comment"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                <Trans>Cancel</Trans>
              </Button>
            </DialogClose>
            <Button variant="default" type="submit" disabled={loading}>
              {loading && <LoadingIcon className={"h-5 w-4 animate-spin"} />}
              <Trans>Report</Trans>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
